<template>
  <component
    :is="eventModalComponent.component"
    v-if="eventModalComponent?.component != null"
    :flag-types="flagTypes"
    :event-type="eventType"
    v-bind="eventModalComponent.props"
    @save="$emit('save', $event)"
  />
</template>

<script setup lang="ts">
import { computed, type PropType } from "vue";
import { type EventListEventFields_AuditResult_Fragment, EventType, FlagTypes } from "@/graphql";
import FailureNoticeModalComponent from "@/modules/ih/components/IHFailureNoticeModalComponent.vue";
import MaintenanceModalComponent from "@/modules/ih/components/IHMaintenanceModalComponent.vue";
import CommonIssueModalComponent from "../../issue/modal/CommonIssueModalComponent.vue";
import QAQualityAssuranceComponent from "@/modules/quality/components/QAQualityAssuranceComponent.vue";
import HLPIdeaModalComponent from "@/modules/idea/components/eab/HLPIdeaModalComponent.vue";
import injectAsserted from "@/functions/assert/injectAsserted";
import { CreateInjectionKey } from "../../issue/IssueTypes";
import type {
  EventEditType,
  IdeaEditType,
  IHFailureNoticeEditType,
  IHMaintenanceEditType,
  IssueEditType,
  QualityAssuranceEditType,
} from "./EventEditType";

type EventComponentType<T extends EventType> = T extends EventType.Issue
  ? {
      component: typeof CommonIssueModalComponent;
      props: { event: IssueEditType };
    }
  : T extends EventType.AuditResult
    ? { component: undefined; event: EventListEventFields_AuditResult_Fragment }
    : T extends EventType.HLPIdea
      ? { component: typeof HLPIdeaModalComponent; props: { event: IdeaEditType } }
      : T extends EventType.IHFailureNotice
        ? {
            component: typeof FailureNoticeModalComponent;
            props: { event: IHFailureNoticeEditType };
          }
        : T extends EventType.IHMaintenance
          ? {
              component: typeof MaintenanceModalComponent;
              props: { event: IHMaintenanceEditType };
            }
          : T extends EventType.QualityAssurance
            ? {
                component: typeof QAQualityAssuranceComponent;
                props: { event: QualityAssuranceEditType };
              }
            : undefined;

const props = defineProps({
  event: {
    type: Object as PropType<EventEditType>,
    required: true,
  },
  eventType: {
    type: String,
    required: true,
  },
  issueType: {
    type: String,
    default: null,
  },
});
defineEmits(["save"]);

const create = injectAsserted(CreateInjectionKey);
const flagTypes = computed(() => {
  if (create.value) {
    return [FlagTypes.userFlag, FlagTypes.mailDistribution, FlagTypes.ideaWorld];
  }
  return [FlagTypes.userFlag, FlagTypes.mailDistribution];
});
const eventModalComponent = computed(() => {
  switch (props.eventType) {
    case EventType.Issue:
      if (props.issueType === "common") {
        return {
          component: CommonIssueModalComponent,
          props: {
            event: props.event,
          },
        } as EventComponentType<EventType.Issue>;
      }
      throw new Error("unknown issue type");
    case EventType.IHFailureNotice:
      return {
        component: FailureNoticeModalComponent,
        props: {
          event: props.event,
        },
      } as EventComponentType<EventType.IHFailureNotice>;
    case EventType.IHMaintenance:
      return {
        component: MaintenanceModalComponent,
        props: {
          event: props.event,
        },
      } as EventComponentType<EventType.IHMaintenance>;
    case EventType.HLPIdea:
      return {
        component: HLPIdeaModalComponent,
        props: {
          event: props.event,
        },
      } as EventComponentType<EventType.HLPIdea>;
    case EventType.QualityAssurance:
      return {
        component: QAQualityAssuranceComponent,
        props: {
          event: props.event,
        },
      } as EventComponentType<EventType.QualityAssurance>;
    default:
      throw new Error("unknown event type EventModalComponent");
  }
});

defineOptions({
  inheritAttrs: true,
});
</script>

<style lang="scss" scoped></style>
